import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  Handle,
  OnReconnect,
  Route,
  WebsocketRouteService
} from "active-connect-ng2";
import { Observable, Subject } from "rxjs";
import { BarcodeDimensions } from "../models/barcode-dimensions";
import { ClosedDay } from "../models/closed-day";
import { FeeGroup } from "../models/fee-group";
import { FeeGroupEntry } from "../models/fee-group-entry";
import { Library } from "../models/library";
import { LoginResponse } from "../models/login-response";
import { Media } from "../models/media";
import { MediaClass } from "../models/media-class";
import { MediaLocation } from "../models/media-location";
import { MediaTaken } from "../models/media-taken";
import { MediaType } from "../models/media-type";
import { Notice } from "../models/notice";
import { OpeningHours } from "../models/opening-hours";
import { PublicFile } from "../models/public-file";
import { PublicLibrary } from "../models/public-library";
import { Reader } from "../models/reader";
import { ReaderClass } from "../models/reader-class";
import { Reservation } from "../models/reservation";
import { Roster } from "../models/roster";
import { Stocktake } from "../models/stocktake";
import { User } from "../models/user";
import { ApplicationPool } from "./application.pool";
import { NetworkLocation } from "../models/network-location";
import {
  LibraryNetworkMedium,
  LibraryNetworkRental
} from "../public/search/medium";
import { Globals } from "./globals";
import { Payment } from "../models/payment";
import { MediaDetail } from "../models/media-detail";
import { Event } from "../models/event";
import { Article } from "../models/article";
import { FeeGroupReminderFee } from "../models/fee-group-reminder-fee";
import { SbTerminal } from "../models/sb-terminal";

@Injectable()
export class ActionService implements WebsocketRouteService {
  private prototype = ApplicationPool.prototype as any;
  private get data(): any {
    return (this.client as any).___data || this.prototype.___data;
  }

  constructor(
    public client: ApplicationPool,
    private router: Router
  ) {
    if (!(ActionService.prototype as any).___data) {
      (ActionService.prototype as any).___data = {};
    }
    (ActionService.prototype as any).___data._obj = this;
  }

  static errorMessage: string;

  private static terminalMessageCallback: Function | null;
  public setTerminalMessageCallback(callback: Function) {
    ActionService.terminalMessageCallback = callback;
  }

  @Handle("auth.loginwithtoken")
  protected tokenLoginResponse(response: LoginResponse) {
    if (response.status != 1) {
      this.router.navigate(["/login"], { replaceUrl: true });
    }
  }
  @Handle("auth.login")
  protected loginResponse(response: LoginResponse) {
    if (response?.status == 1) {
      if (response.token) this.client.Token = response.token;
      if (
        this.router.isActive("/login", false) ||
        this.router.isActive("/public", false) ||
        (this.router.isActive("/signin", false) &&
          !document.location.href.includes("/stocktake/"))
      ) {
        this.router.navigate(["/"], { replaceUrl: true });
      } else if (document.location.href.includes("signin/stocktake")) {
        console.log("redirecting to stocktake");
        const parts = document.location.href.split("/");
        let path = "/stocktake/" + parts[parts.length - 1];
        this.router.navigate([path], { replaceUrl: true });
      }
      return;
    } else if (response?.status == -1) {
      this.client.clearVarsAfterLogout();

      if (
        document &&
        document.location.href.indexOf("/login/") < 0 &&
        document.location.href.indexOf("/public/") < 0 &&
        document.location.href.indexOf("/mail-settings") < 0
      ) {
        this.router.navigate(["/public/info"], { replaceUrl: true });
      }
    }

    if (this.prototype.loading && this.prototype.loading.clear) {
      this.prototype.loading.clear();
    }
  }

  @Handle("terminal.message")
  public handleMessage(msg: string) {
    if (ActionService.terminalMessageCallback)
      ActionService.terminalMessageCallback(msg);
  }

  @Handle("m.auth.token")
  public handleAuth(res: boolean) {}

  @Handle("auth.cleartoken")
  async clearSessionToken() {}

  private static errorSubject: Subject<any> = new Subject();
  onError(): Observable<any> {
    return ActionService.errorSubject.asObservable();
  }

  @Handle("m.error")
  public async handleError(msg: string) {
    ActionService.errorMessage = msg;
    ActionService.errorSubject.next(msg);

    // reset loading animation
    const loading = (this.client as any).loading;
    Object.keys(loading).forEach((v) =>
      loading.set ? loading.set(v, false) : (loading[v] = 0)
    );
  }

  @Handle("d.rental.insert")
  public async handleTakenInsert(mt1: MediaTaken[] | MediaTaken) {
    const mt = mt1 instanceof Array ? mt1[0] : mt1;
    this.data.ActiveMediaTaken?.unshift(mt);
  }
  @Handle("d.rental.update")
  public async handleTakenUpdate(mt1: MediaTaken[] | MediaTaken) {
    const mt = mt1 instanceof Array ? mt1[0] : mt1;
    const entries = this.data.ActiveMediaTaken?.filter(
      (m: MediaTaken) => m.id == mt.id
    ) as MediaTaken[];
    if (entries.length > 0) entries[0].latestReturnDate = mt.latestReturnDate;
  }
  @Handle("d.rental.delete")
  public async handleTakenDelete(id1: number[] | number) {
    const id = id1 instanceof Array ? id1[0] : id1;

    const entries = this.data.ActiveMediaTaken?.filter(
      (m: MediaTaken) => m.id == id
    );
    if (entries && entries.length > 0) {
      const i = this.data.ActiveMediaTaken?.indexOf(entries[0]);
      if (i >= 0) this.data.ActiveMediaTaken.splice(i, 1);
    }
  }
  @Handle("d.reader.update")
  public async handleReaderUpdate(reader: Reader) {
    console.log(`READER UPDATE reader #${reader.id} balance=${reader.balance}`);
    if (reader) {
      const entries = this.data.Readers?.filter(
        (r1: Reader) => r1.id == reader.id
      ) as Reader[];
      if (entries?.length > 0) {
        entries[0].balance = reader.balance;
        if (reader.feeGroupValidUntil) {
          entries[0].feeGroupValidUntil;
        }
      }
    }
  }

  @Handle("d.stocktake.media")
  public async handleStocktakeUpdate(id1: number[] | number) {
    const id = id1 instanceof Array ? id1[0] : id1;

    if (this.client.Stocktakes.all) {
      this.client.Stocktakes.all.forEach(
        (s: Stocktake) => (s.medias = s.medias?.filter((m) => m.id != id))
      );
    }
  }

  @Route("auth.login")
  public async login(user: User): Promise<LoginResponse> {
    return new LoginResponse(0);
  }

  @Route("auth.logout")
  public async logout(): Promise<any> {
    this.client.Token = "";
    this.client.clearVarsAfterLogout();
  }

  @Route("auth.token")
  public async auth(token: string): Promise<any> {
    this.client.Token = token;
  }

  @Route("rental.take", "rental")
  public async takeMedia(mediaTaken: MediaTaken): Promise<number> {
    return 0;
  }
  @Route("rental.extend", "rental")
  public async extendMediaTaken(mediaTaken: MediaTaken): Promise<number> {
    return 0;
  }

  @Route("rental.return", "rental")
  public async returnMedia(mediaTaken: MediaTaken): Promise<number> {
    return 0;
  }
  @Route("rental.hasbeentaken", "rental")
  public async hasMediaBeenTaken(mediaTaken: MediaTaken): Promise<number> {
    return 0;
  }

  @Route("rental.countvisitor", "rental")
  public async countVisitor() {}

  @Route("rental.reservation.take", "rental")
  public async takeReservation(reservation: Reservation): Promise<number> {
    return 0;
  }

  @Route("rental.media.update", "medias")
  public async saveMedia(media: MediaDetail): Promise<number> {
    return 0;
  }

  @Route("rental.media.insert", "medias")
  public async createMedia(media: MediaDetail): Promise<number> {
    return 0;
  }

  @Route("rental.media.insertmultiple", "medias")
  public async createMultipleMedia(media: MediaDetail[]): Promise<number[]> {
    return [];
  }

  @Route("rental.media.unhide", "medias")
  public async unhideMedia(media: MediaDetail): Promise<any> {}

  @Route("rental.media.delete", "medias")
  public async deleteMedia(media: Media): Promise<number> {
    return 0;
  }
  @Route("rental.media.deletemultiple", "medias")
  public async deleteMultipleMedia(media: Media[]): Promise<number> {
    return 0;
  }

  @Route("rental.media.top", "medias")
  public async getTopMedia(mediaClass: MediaClass | null): Promise<Media[]> {
    return [];
  }

  @Route("rental.reader.update", "readers")
  public async saveReader(reader: Reader): Promise<number> {
    return 0;
  }
  @Route("rental.reader.setfeegroup", "readers")
  public async setReaderFeeGroup(reader: Reader): Promise<number> {
    return 0;
  }
  @Route("rental.reader.insert", "readers")
  public async createReader(reader: Reader): Promise<number> {
    return 0;
  }
  @Route("rental.reader.delete", "readers")
  public async deleteReader(reader: Reader): Promise<number> {
    return 0;
  }
  @Route("rental.reader.anonymize", "readers")
  async anonymizeReaderData(reader: Reader): Promise<any> {}

  @Route("rental.reader.acceptemail")
  public async acceptEmailChangeRequest(reader: Reader): Promise<any> {}
  @Route("rental.reader.rejectemail")
  public async rejectEmailChangeRequest(reader: Reader): Promise<any> {}

  @Route("rental.reader.statistics")
  public async requestReaderStatistics(reader: Reader): Promise<any> {}

  @Route("rental.reader.history.request")
  public async requestRentalHistory({
    reader,
    limit
  }: {
    reader: Reader;
    limit: number;
  }): Promise<MediaTaken[]> {
    return [];
  }

  @Route("rental.media.statistics")
  public async requestMediaStatistics(media: Media): Promise<any> {}

  @Route("rental.media.history.request")
  public async requestMediaHistory(media: Media): Promise<MediaTaken[]> {
    return [];
  }

  @Route("rental.readerclass.update", "readerClass")
  public async saveReaderClass(readerClass: ReaderClass): Promise<number> {
    return 0;
  }

  @Route("rental.readerclass.insert", "readerClass")
  public async createReaderClass(readerClass: ReaderClass): Promise<number> {
    return 0;
  }

  @Route("rental.readerclass.delete", "readerClass")
  public async deleteReaderClass(readerClass: ReaderClass): Promise<number> {
    return 0;
  }

  @Route("rental.user.update", "users")
  public async saveUser(user: User): Promise<number> {
    return 0;
  }

  @Route("auth.user.save", "users")
  async saveAuthUser(user: User): Promise<any> {}
  @Route("auth.otp.fetch")
  async fetchOtpUrl(): Promise<any> {}
  @Route("auth.otp.setup")
  async finishOtpSetup(req: {
    secret: string;
    confirmation: string;
  }): Promise<any> {}

  @Route("rental.user.insert", "users")
  public async createUser(user: User): Promise<number> {
    return 0;
  }
  @Route("rental.user.delete", "users")
  public async deleteUser(user: User): Promise<number> {
    return 0;
  }
  @Route("rental.mediaclass.update", "mediaClass")
  public async saveMediaClass(mediaClass: MediaClass): Promise<number> {
    return 0;
  }
  @Route("rental.mediaclass.delete", "mediaClass")
  public async deleteMediaClass(mediaClass: MediaClass): Promise<number> {
    return 0;
  }
  @Route("rental.mediaclass.insert", "mediaClass")
  public async createMediaClass(mediaClass: MediaClass): Promise<number> {
    return 0;
  }
  @Route("rental.mediatype.update", "mediaTypes")
  public async saveMediaType(mediaType: MediaType): Promise<number> {
    return 0;
  }
  @Route("rental.mediatype.insert", "mediaTypes")
  public async createMediaType(mediaType: MediaType): Promise<number> {
    return 0;
  }
  @Route("rental.mediatype.delete", "mediaTypes")
  public async deleteMediaType(mediaType: MediaType): Promise<number> {
    return 0;
  }
  @Route("rental.medialocation.update", "mediaLocations")
  public async saveMediaLocation(
    mediaLocation: MediaLocation
  ): Promise<number> {
    return 0;
  }
  @Route("rental.medialocation.insert", "mediaLocations")
  public async createMediaLocation(
    mediaLocation: MediaLocation
  ): Promise<number> {
    return 0;
  }
  @Route("rental.medialocation.delete", "mediaLocations")
  public async deleteMediaLocation(
    mediaLocation: MediaLocation
  ): Promise<number> {
    return 0;
  }
  @Route("rental.reservation.insert", "reservations")
  public async reservateMedia(reservation: Reservation): Promise<number> {
    return 0;
  }
  @Route("rental.reservation.delete", "reservations")
  public async deleteReservation(reservation: Reservation): Promise<number> {
    return 0;
  }

  @Route("rental.stocktake.start", "stocktakes")
  public async startStocktake(): Promise<number> {
    return 0;
  }

  @Route("rental.stocktake.present")
  public async setMediaPresent(media: Media): Promise<number> {
    return 0;
  }

  @Route("rental.settings.barcodedimensions.update", "library")
  public async saveBarcodeDimensions(
    barcodeDimensions: BarcodeDimensions
  ): Promise<number> {
    return 0;
  }

  @Route("rental.settings.signaturedimensions.update", "library")
  public async saveSignatureDimensions(
    signatureDimensions: BarcodeDimensions
  ): Promise<number> {
    return 0;
  }

  @Route("rental.feegroup.saveentry", "feegroups")
  async saveFeeGroupEntry(entry: FeeGroupEntry): Promise<any> {}
  @Route("rental.feegroup.save", "feegroups")
  async saveFeeGroup(group: FeeGroup): Promise<any> {}
  @Route("rental.feegroup.delete", "feegroups")
  async deleteFeeGroup(group: FeeGroup): Promise<any> {}
  @Route("rental.feegroup.savereminderfee", "feegroups")
  async saveFeeGroupReminderFee(group: FeeGroupReminderFee): Promise<any> {}
  @Route("rental.feegroup.deletereminderfee", "feegroups")
  async deleteFeeGroupReminderFee(group: FeeGroupReminderFee): Promise<any> {}

  @Route("rental.pay", "balance")
  public async pay(data: {
    reader: Reader;
    amount: number;
    remitResidual: boolean;
    donation: number | null;
  }): Promise<number> {
    return 0;
  }

  @Route("rental.payment.insert")
  async insertPayment(payment: Payment): Promise<any> {}
  @Route("rental.payment.cancel")
  async cancelPayment(payment: Payment): Promise<any> {}

  @Route("rental.library.update", "library")
  public async saveLibraryConfiguration(library: Library): Promise<number> {
    return 0;
  }

  @Route("admin.library.update", "library")
  public async adminSaveLibrary(library: Library): Promise<number> {
    return 0;
  }
  @Route("admin.library.insert", "library")
  public async adminCreateLibrary(label: string): Promise<number> {
    return 0;
  }
  @Route("admin.library.persistfee", "library")
  public async adminPersistFee(label: Library): Promise<number> {
    return 0;
  }

  @Route("admin.import")
  public async executeImport(data: {
    readerData: string;
    mediaData: string;
    library: Library;
  }): Promise<any> {}

  @Route("admin.postimport")
  public async executePostimport(data: { library: Library }): Promise<any> {}

  @Route("admin.searchisbn")
  public async executeIsbnSearch(data: { library: Library }): Promise<any> {}

  @Route("admin.restart")
  public async restartServer(): Promise<number> {
    return 0;
  }

  @Route("admin.elasticsearch.recreate")
  public async recreateElasticsearchIndex(): Promise<any> {}

  @Route("admin.triggermedia2go")
  public async triggerMedia2GoExport(library: Library): Promise<number> {
    return 0;
  }

  @Route("auth.usertoken")
  public async loginWithUserToken(token: string): Promise<number> {
    return 0;
  }

  @Route("admin.library.notification")
  async adminSetNotification(notification: string): Promise<any> {}

  @Route("admin.connection.get")
  async getAllConnections(): Promise<any | string[]> {}

  @Route("public.library.select")
  async selectLibrary(library: Library) {}

  @Route("public.search.exec")
  async executePublicSearch({
    search,
    sort,
    mediaType,
    locationId,
    elasticsearch
  }: {
    search: string;
    sort: number;
    mediaType: string | null;
    locationId: number | null;
    elasticsearch: boolean;
  }): Promise<any> {}
  @Route("public.search.fetch")
  async fetchMedia({
    id,
    locationId,
    isbn
  }: {
    id: number;
    locationId: number | undefined;
    isbn: string | null;
  }): Promise<any> {}

  @Route("rental.openinghours.save", "openingHours")
  async saveOpeningHours(elem: OpeningHours): Promise<any> {}
  @Route("rental.openinghours.delete", "openingHours")
  async deleteOpeningHours(elem: OpeningHours): Promise<any> {}

  @Route("rental.closedday.save", "closedDays")
  async saveClosedDay(elem: ClosedDay): Promise<any> {}
  @Route("rental.closedday.delete", "closedDays")
  async deleteClosedDay(elem: ClosedDay): Promise<any> {}

  @Route("rental.roster.save")
  async saveRoster(elem: Roster): Promise<any> {}
  @Route("rental.roster.delete")
  async deleteRoster(elem: Roster): Promise<any> {}

  @Route("rental.notice.save", "notices")
  async saveNotice(elem: Notice): Promise<any> {}
  @Route("rental.notice.delete", "notices")
  async deleteNotice(elem: Notice): Promise<any> {}

  @Route("rental.cashbook.get")
  async getCashBookForPeriod(period: {
    startDate: Date;
    endDate: Date;
  }): Promise<any> {}

  @Route("rental.media.getflow")
  async getMediaFlowForPeriod(period: {
    startDate: Date;
    endDate: Date;
    delete: boolean;
  }): Promise<any> {}

  @Route("rental.getforperiod")
  async getMediaTakenForPeriod(period: {
    startDate: Date;
    endDate: Date;
  }): Promise<any> {}

  @Route("rental.activity")
  async getReaderActivity(year: number): Promise<any> {}

  @Route("rental.statistic.get")
  async getRentalStatistic(year: number): Promise<any> {}

  @Route("auth.reset.password")
  async requestPasswordReset(data: {
    library: Library;
    email: string;
  }): Promise<any> {}

  @Route("auth.reset.exists")
  async isPasswordKeyValid(key: string): Promise<boolean> {
    return false;
  }

  @Route("auth.reset.reset")
  async resetPassword(req: { password: string; key: string }): Promise<any> {}

  @Route("public.search.log")
  async onSearchOpened(): Promise<void> {}

  @Route("public.login.reader")
  async loginReader(data: {
    id: number;
    birthdate: string;
    otp?: number;
  }): Promise<any> {}

  @Route("reader.taken.extend")
  async extendByReader(rental: LibraryNetworkRental): Promise<any> {}

  @Route("reader.taken.isextendable")
  async isRentalExtendable(rental: LibraryNetworkRental): Promise<boolean> {
    return false;
  }

  @Route("reader.reservation.reserve")
  async reserve(medium: LibraryNetworkMedium): Promise<any> {}
  @Route("reader.reservation.cancel")
  async cancelReservation(medium: LibraryNetworkMedium): Promise<any> {}

  @Route("reader.media2go.auth")
  async getMedia2GoAuthenticationUrl(data: {
    library: PublicLibrary;
    reader: Reader;
  }): Promise<any> {}

  @Route("reader.reader.submitemail")
  async submitEmailByReader(reader: Reader): Promise<any> {}

  @Route("rental.notice.read")
  async setNoticeStatusRead(notice: Notice): Promise<any> {}

  @Route("rental.library.content", "library")
  async saveLibraryContent(content: PublicLibrary): Promise<any> {}
  @Route("rental.library.image")
  async uploadLibraryLogoImage(dataUrl: string): Promise<any> {}

  @Route("rental.reader.extendfeegroup", "readers")
  async extendFeeGroup({
    reader,
    date
  }: {
    reader: Reader;
    date: Date | null;
  }): Promise<any> {}
  @Route("rental.reader.scheduleextensionemail")
  async scheduleFeeGroupExtensionEmail(): Promise<any> {}

  @Route("rental.reader.scheduleextensionemailforreader")
  async scheduleFeeGroupExtensionEmailForReader(reader: Reader): Promise<any> {}

  @Route("rental.reader.payments", "readers")
  async getTransactionsForReader(data: {
    reader: Reader;
    fullHistory: boolean;
  }): Promise<any> {}

  @Route("rental.mediaclass.batch")
  async batchCreateMediaClassEntries(type: "bvoe2" | "bvoe3"): Promise<any> {}

  @Route("rental.publicfile.upload", "publicfiles")
  async uploadPublicFile(file: PublicFile): Promise<any> {}
  @Route("rental.publicfile.delete", "publicfiles")
  async deletePublicFile(file: PublicFile): Promise<any> {}

  @Route("rental.event.save", "events")
  async saveEvent(event: Event): Promise<any> {}
  @Route("rental.event.delete", "events")
  async deleteEvent(event: Event): Promise<any> {}

  @Route("rental.article.save", "articles")
  async saveArticle(article: Article): Promise<any> {}
  @Route("rental.article.uploadimage", "articles")
  async uploadArticleImage({
    articleId,
    dataUrl
  }: {
    articleId: number;
    dataUrl: string;
  }): Promise<any> {}
  @Route("rental.article.deleteimage", "articles")
  async deleteArticleImage(imageId: number): Promise<any> {}
  @Route("rental.article.delete", "articles")
  async deleteArticle(article: Article): Promise<any> {}

  @Route("rental.roster.history")
  async getRosterHistory(period: {
    startDate: Date;
    endDate: Date;
  }): Promise<any> {}

  @Route("rental.reader.transfer", "readers")
  async transferReader({
    reader,
    target
  }: {
    reader: Reader;
    target: NetworkLocation;
  }): Promise<void> {}

  @Route("admin.network.transferreader")
  public async forceTransferLibraryNetworkReaders(data: {
    library: Library;
  }): Promise<any> {}

  @Route("public.mailsubscription.config")
  public async getMailSubscriptionConfig(data: {
    type: "user" | "reader";
    auth: string;
  }): Promise<any> {}

  @Route("support.send")
  public async sendSupportMessage(data: {
    type: string;
    client: any;
    name: string;
    email: string;
    description: string;
    wsMessages: {
      timestamp: Date;
      method: string;
    }[];
    consoleLogs: string[];
    screenshot: string | undefined;
  }): Promise<any> {}

  @Handle("m.public.mailsubscription.config")
  onGetMailSubscriptionConfig(config: any) {
    this.mailSubscriptionConfig = config;
  }

  public mailSubscriptionConfig!: {
    user?: User;
    reader?: Reader;
    options: { label: string; enabled: boolean };
  } | null;

  @Route("public.mailsubscription.update")
  public async updateMailSubscriptionConfig({
    type,
    auth,
    options
  }: {
    type: "user" | "reader";
    auth: string;
    options: { label: string; enabled: boolean };
  }): Promise<any> {}

  @Route("rental.sb.pin")
  async getSbPin(sb: SbTerminal): Promise<any> {}

  @Route("rental.sb.save")
  async updateSbTerminal(sb: SbTerminal): Promise<any> {}

  @Route("reader.push.subscribe")
  async subscribeForPushNotifications(subscription: {
    endpoint: string;
  }): Promise<any> {}

  @Route("rental.reader.testpush", "push")
  async sendTestPushNotification(reader: Reader): Promise<any> {}

  // REGION SB
  @Route("sb.auth")
  async sbAuth(pin: number): Promise<any> {}
  @Route("sb.rental.take")
  async sbCreateRental({
    readerId,
    mediaId
  }: {
    readerId: number;
    mediaId: number;
  }): Promise<any> {}
  @Route("sb.rental.return")
  async sbReturnRental(mediaId: number): Promise<any> {}
  @Route("sb.rental.extend")
  async sbExtendRental(mediaTakenId: number): Promise<any> {}

  @OnReconnect
  async onWebsocketReconnected() {
    if (
      Globals.LastSelectedLibraryId &&
      +Globals.LastSelectedLibraryId &&
      +Globals.LastSelectedLibraryId > 0
    ) {
      if (!document.location.href.includes("/select-library/")) {
        await this.selectLibrary(
          new Library(Globals.LastSelectedLibraryId, true, "")
        ).catch(() => {
          this.router.navigate(["/"]);
        });
      }
    }
  }

  public loadingElements: any = {};
}
